<script setup lang="ts">
import type { AccordionItem, FormSubmitEvent } from '#ui/types';
import { merge } from 'es-toolkit/compat';

const props = defineProps<{
  action: 'create' | 'edit'
  data: Partial<Apikey>
  onSubmit: (event: FormSubmitEvent<Partial<Apikey>>) => void
  openNestedCallback: (data: Partial<Apikey>) => void
  title?: string
  disabledFields?: (keyof Apikey | string)[]
  readonly?: boolean
}>()

const { user } = useUserSession()
const { userModule } = useModules()
const slideover = useSlideover()

const inert = computed(() => props.readonly || props.data?.immutable || !['admin', 'edit'].includes(userModule.value?.role))

const state = ref<Partial<Apikey>>(props.data)

const apikeys$ = useApikeys()

const items = computed<AccordionItem[]>(() => {
  return [
    {
      slot: 'general',
      label: 'General',
      icon: 'i-mdi-information',
      defaultOpen: true,
    },
  ]
})

const loading = ref(false)

const onSubmitApikey = async (event: FormSubmitEvent<Partial<Apikey>>) => {
  event.data = merge(state.value, event.data) // WORKAROUND: UForm drops nested props on validation
  loading.value = true
  await props.onSubmit(event)
}
</script>

<template>
  <UDashboardSlideover prevent-close :ui="{ width: 'min-w-[40vw]' }">
    <template #title>
      <SlideoverTitle :title="title" :inert="inert" />
    </template>
    <UForm
      id="form.apikeys"
      :validate-on="['submit']"
      :schema="apikeys$.schema"
      :state="state"
      :inert="inert"
      @error="onFormError"
      @submit="onSubmitApikey"
    >
      <UAccordion
        multiple
        :items="items"
        :ui="{ item: { base: 'py-1 px-2 space-y-2 text-sm' } }"
      >
        <template #general>
          <UFormGroup
            label="Nombre"
            name="name"
            :required="isRequired(apikeys$.schema, 'name')"
          >
            <InputText
              v-model.defaultcase="state.name"
              autofocus
              :disabled="disabledFields?.includes('name')"
              placeholder="NETZO_API_KEY"
            />
          </UFormGroup>

          <UFormGroup
            label="Descripción"
            name="description"
            :required="isRequired(apikeys$.schema, 'description')"
          >
            <UTextarea
              v-model="state.description"
              :rows="5"
              autoresize
            />
          </UFormGroup>

          <UFormGroup
            label="Permiso"
            name="permission"
            :required="isRequired(apikeys$.schema, 'permission')"
          >
            <SelectMenuBaseInfo
              v-model="state.permission"
              :options="optionsApikeys.permission"
              :disabled="disabledFields?.includes('permission')"
            />
          </UFormGroup>
        </template>
      </UAccordion>
    </UForm>

    <template v-if="!inert" #footer>
      <div class="grid grid-cols-2 gap-2 w-full">
        <UButton
          color="gray"
          label="Cancelar"
          block
          @click="slideover.close()"
        />
        <UButton
          form="form.apikeys"
          type="submit"
          label="Confirmar"
          color="primary"
          block
          :loading="loading"
        />
      </div>
    </template>
  </UDashboardSlideover>
</template>
